"use client";

import { useSyncExternalStore } from "react";
import type { LookScheme } from "@/types/look-system";
import type { LookSwitchProps } from "@/utils/look-scheme";

const subscribe = (notify: () => void) => {
  const observer = new MutationObserver(notify);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-re-scheme"],
  });
  return () => observer.disconnect();
};
const currentScheme = (): LookScheme =>
  document.documentElement.dataset.reScheme === "dark" ? "dark" : "light";
const serverScheme = () => undefined;

/**
 * The visitor's light/dark switch on the look system. Its icon follows the document's scheme through CSS, so it is
 * right before hydration. A header passes the state attributes its colour follows, like the header's other icons.
 */
export default function LookSchemeSwitch({
  placement,
  cookieName,
  className,
  ...state
}: LookSwitchProps & {
  className?: string;
  "data-isvisible"?: boolean;
  "data-menu-open"?: boolean;
  suppressHydrationWarning?: boolean;
}) {
  const scheme = useSyncExternalStore(subscribe, currentScheme, serverScheme);
  const toggle = () => {
    const next: LookScheme = currentScheme() === "dark" ? "light" : "dark";
    document.documentElement.dataset.reScheme = next;
    document.cookie = `${cookieName}=${encodeURIComponent(JSON.stringify({ scheme: next }))}; Path=/; Max-Age=31536000; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`;
  };
  return (
    <button
      type="button"
      {...state}
      data-re-scheme-switch={placement}
      aria-label="Mørk tilstand"
      aria-pressed={scheme === undefined ? undefined : scheme === "dark"}
      onClick={toggle}
      className={`inline-flex size-10 shrink-0 items-center justify-center rounded-full${className ? ` ${className}` : ""}`}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="re-scheme-light size-6"
      >
        <path d="M20.5 14.5A8.5 8.5 0 0 1 9.5 3.5a8.5 8.5 0 1 0 11 11z" />
      </svg>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="re-scheme-dark size-6"
      >
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
      </svg>
    </button>
  );
}
